// Static imports of the recorded demo receipt bundles.
//
// Each bundle.json is a flat map of relative path → UTF-8 contents for
// one layer's receipt package. Vite/Rollup inline these JSON imports
// into the build regardless of their location on disk (they live in the
// repo's demo-artifacts/ directory, outside this app root — see
// server.fs.allow in vite.config.ts for the dev server).

import L1 from "../../../demo-artifacts/receipts/L1-freeform/bundle.json";
import L2 from "../../../demo-artifacts/receipts/L2-schema/bundle.json";
import L3conformant from "../../../demo-artifacts/receipts/L3-template-conformant/bundle.json";
import L3violation from "../../../demo-artifacts/receipts/L3-template-violation/bundle.json";
import L5 from "../../../demo-artifacts/receipts/L5-countersigned/bundle.json";
import manifestJson from "../../../demo-artifacts/manifest.json";

// The canonical JSON Schemas the L2 evidence blobs are validated against.
// Imported statically (like the bundles above) so the schema viewer is part
// of the self-contained bundle and does not depend on a runtime fetch. These
// are byte-for-byte the same files the verifier loads from schemas/action/,
// which are also served at /schemas/action/... in dev and copied into dist/
// at build (see vite.config.ts).
import taskCreatedSchema from "../../../schemas/action/task_created.v0.1.schema.json";
import policyCheckedSchema from "../../../schemas/action/policy_checked.v0.1.schema.json";
import llmInvoiceReviewedSchema from "../../../schemas/action/llm_invoice_reviewed.v0.1.schema.json";
import paymentInstructionCreatedSchema from "../../../schemas/action/payment_instruction_created.v0.1.schema.json";

export type Bundle = Record<string, string>;

export interface DemoSchema {
  schemaId: string;
  actionType: string;
  // Repo-relative path the verifier resolves this schema from.
  path: string;
  schema: Record<string, unknown>;
}

// The four evidence schemas declared by the L2 (schema-validated) receipt,
// in chain order. `schema` is the parsed JSON; `path` is the exact
// registry-relative location the verifier reads it from.
export const L2_SCHEMAS: DemoSchema[] = [
  {
    schemaId: "sequesign.task_created.v0.1",
    actionType: "task_created",
    path: "schemas/action/task_created.v0.1.schema.json",
    schema: taskCreatedSchema as Record<string, unknown>
  },
  {
    schemaId: "sequesign.policy_checked.v0.1",
    actionType: "policy_checked",
    path: "schemas/action/policy_checked.v0.1.schema.json",
    schema: policyCheckedSchema as Record<string, unknown>
  },
  {
    schemaId: "sequesign.llm_invoice_reviewed.v0.1",
    actionType: "llm_invoice_reviewed",
    path: "schemas/action/llm_invoice_reviewed.v0.1.schema.json",
    schema: llmInvoiceReviewedSchema as Record<string, unknown>
  },
  {
    schemaId: "sequesign.payment_instruction_created.v0.1",
    actionType: "payment_instruction_created",
    path: "schemas/action/payment_instruction_created.v0.1.schema.json",
    schema: paymentInstructionCreatedSchema as Record<string, unknown>
  }
];

// The policy_checked schema, singled out for the two L2 demonstrations that
// need it: the schema viewer's hash-binding note (task C), the seal-time
// refusal (task E), and the "tamper the schema" break (task F).
export const POLICY_CHECKED_SCHEMA = policyCheckedSchema as Record<string, unknown>;

// The agent's actual raw output for the L0 "without Sequesign" screen: the
// content of the payment_instruction_created evidence, read verbatim from the
// L1 bundle. This is the unverifiable transcript L0 is meant to expose.
function readRawAgentOutput(): unknown {
  const key = Object.keys(L1 as Bundle).find((k) => k.includes("payment-instruction"));
  if (!key) return null;
  try {
    const blob = JSON.parse((L1 as Bundle)[key]) as { content?: unknown };
    return blob.content ?? null;
  } catch {
    return null;
  }
}

export const RAW_AGENT_OUTPUT: unknown = readRawAgentOutput();

// Keyed by the demo-artifacts folder name (the layerDataId in the
// scenario). L0 has no receipt and therefore no entry.
export const BUNDLES: Record<string, Bundle> = {
  "L1-freeform": L1 as Bundle,
  "L2-schema": L2 as Bundle,
  "L3-template-conformant": L3conformant as Bundle,
  "L3-template-violation": L3violation as Bundle,
  "L5-countersigned": L5 as Bundle
};

export interface ManifestLayer {
  layer: string;
  title: string;
  dir: string;
  valid: boolean;
  conformant: boolean | null;
  flags: Record<string, unknown>;
  violations: string[];
}

export interface Manifest {
  layers: ManifestLayer[];
}

export const MANIFEST = manifestJson as Manifest;
